/**
 * useSyncedPlayer — React hook that wires a real <video> element to the
 * RoomClient (WebSocket) and the SyncEngine, using the browser's WebSocket,
 * timers, performance clock and crypto UUIDs.
 */
import { useEffect, useRef, useState } from "react";
import { RoomClient, type SocketLike, type Scheduler } from "../sync/roomClient.ts";
import { SyncEngine, type EngineStatus } from "./syncEngine.ts";
import { attachHls, type HlsHandle, type QualityLevel } from "./hlsController.ts";
import { WORKER_WS_BASE } from "../config.ts";

const browserScheduler: Scheduler = {
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (id) => window.clearTimeout(id),
  setInterval: (fn, ms) => window.setInterval(fn, ms),
  clearInterval: (id) => window.clearInterval(id),
};

/** A clientId that survives reloads within the same tab (sessionStorage), so a
 * reconnect reclaims its own room slot. Falls back to a fresh id if storage is
 * unavailable (private mode / disabled). */
function stableClientId(roomId: string): string {
  const fresh = () =>
    globalThis.crypto?.randomUUID?.() ?? `c-${Math.floor(performance.now())}-${Math.round(performance.timeOrigin)}`;
  const key = `mov_client_${roomId}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = fresh();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return fresh();
  }
}

/** Adapt a browser WebSocket to the SocketLike interface used by RoomClient. */
function connectSocket(roomId: string): SocketLike {
  const ws = new WebSocket(`${WORKER_WS_BASE}/ws?room=${encodeURIComponent(roomId)}`);
  const sock: SocketLike = {
    get readyState() {
      return ws.readyState;
    },
    send: (data) => ws.send(data),
    close: () => ws.close(),
    onopen: null,
    onclose: null,
    onerror: null,
    onmessage: null,
  };
  ws.onopen = () => sock.onopen?.();
  ws.onclose = () => sock.onclose?.();
  ws.onerror = (e) => sock.onerror?.(e);
  ws.onmessage = (e) => sock.onmessage?.(typeof e.data === "string" ? e.data : "");
  return sock;
}

export interface SyncedPlayerState {
  status: EngineStatus | null;
  connected: boolean;
  peerConnected: boolean;
  isReference: boolean;
  levels: QualityLevel[];
  currentHeight: number | null;
  autoQuality: boolean;
  /** -1 = auto, otherwise the hls.js level index the user pinned. */
  selectedLevel: number;
  watchdog: boolean;
  /** true when the browser blocked autoplay — prompt the user to press play. */
  needsGesture: boolean;
  setLevel: (index: number) => void;
}

export function useSyncedPlayer(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  roomId: string | null,
  src: string | null,
): SyncedPlayerState {
  const [status, setStatus] = useState<EngineStatus | null>(null);
  const [connected, setConnected] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);
  const [isReference, setIsReference] = useState(false);
  const [levels, setLevels] = useState<QualityLevel[]>([]);
  const [currentHeight, setCurrentHeight] = useState<number | null>(null);
  const [autoQuality, setAutoQuality] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(-1);
  const [watchdog, setWatchdog] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);
  const hlsRef = useRef<HlsHandle | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !roomId || !src) return;

    // Stable per-tab clientId for this room: a reload/reconnect presents the SAME
    // id so the room evicts the tab's own ghost socket instead of counting it as
    // a third participant and locking the user out mid-movie.
    const selfId = stableClientId(roomId);

    // eslint-disable-next-line prefer-const
    let engine: SyncEngine;
    const client = new RoomClient(
      {
        clientId: selfId,
        connect: () => connectSocket(roomId),
        now: () => performance.now(),
        uuid: () => globalThis.crypto?.randomUUID?.() ?? `${performance.now()}-${Math.random()}`,
        scheduler: browserScheduler,
      },
      {
        onConnChange: setConnected,
        onWelcome: (state, isRef, peer) => {
          setIsReference(isRef);
          setPeerConnected(peer);
          engine.onWelcome(state, peer);
        },
        onSnapshot: (state) => engine.onSnapshot(state),
        onPresence: (peer, isRef) => {
          setPeerConnected(peer);
          setIsReference(isRef);
          engine.onPresence(peer);
        },
        onControl: (m) => engine.applyRemoteControl(m),
        onHeartbeat: (m) => engine.onRemoteHeartbeat(m),
        onBufferState: (m) => engine.onRemoteBufferState(m),
        onResumeAt: (m) => engine.onRemoteResumeAt(m),
      },
    );

    engine = new SyncEngine({
      media: video,
      transport: client,
      selfId,
      scheduler: browserScheduler,
      now: () => performance.now(),
      uuid: () => globalThis.crypto?.randomUUID?.() ?? `${performance.now()}-${Math.random()}`,
      onStatus: setStatus,
      onWatchdog: () => setWatchdog(true),
      onNeedGesture: () => setNeedsGesture(true),
    });

    setSelectedLevel(-1);
    setAutoQuality(true);
    const hls = attachHls(video, src);
    hlsRef.current = hls;
    hls.onLevelSwitch((height, auto) => {
      setCurrentHeight(height);
      setAutoQuality(auto);
    });
    setLevels(hls.getLevels());

    const onPlay = () => {
      setNeedsGesture(false);
      engine.onMediaPlay();
    };
    const onPause = () => engine.onMediaPause();
    const onSeeked = () => engine.onMediaSeeked();
    const onWaiting = () => engine.mediaWaiting();
    const onPlaying = () => engine.mediaPlaying();
    // `canplay`/`canplaythrough` fire when enough data is buffered even while the
    // element is paused (e.g. held by the buffer barrier) — clears buffering fast.
    const onCanPlay = () => engine.mediaPlaying();
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("canplaythrough", onCanPlay);
    // Levels populate slightly after manifest parse.
    const levelPoll = window.setTimeout(() => setLevels(hls.getLevels()), 1500);

    engine.start();
    client.connect();

    return () => {
      window.clearTimeout(levelPoll);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("canplaythrough", onCanPlay);
      engine.stop();
      client.disconnect();
      hls.destroy();
      hlsRef.current = null;
    };
  }, [videoRef, roomId, src]);

  const setLevel = (index: number) => {
    hlsRef.current?.setLevel(index);
    setSelectedLevel(index);
    setAutoQuality(index === -1);
  };

  return { status, connected, peerConnected, isReference, levels, currentHeight, autoQuality, selectedLevel, watchdog, needsGesture, setLevel };
}
